import * as React from "react";
import { Outlet } from "react-router-dom";
import { SidebarLayout, SidebarProvider } from "@/components/layout/sidebar/Sidebar";
import { createSidebarItems } from "@/components/layout/sidebar/menuConfig";
import Navbar from "@/components/layout/Navbar";
import { useIsIncludeCompany, useUser } from "@/stores/authStore";
import { useEffectiveDocumentDir } from "@/hooks/useEffectiveDocumentDir";
import usePermission from "@/hooks/usePermission";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

export default function MainLayout() {
  const { t, i18n } = useTranslation();
  const shellDir = useEffectiveDocumentDir();
  const baseLang = String(i18n.resolvedLanguage ?? i18n.language ?? "en")
    .split(/[-_/]/)[0]
    .toLowerCase();
  const useRtlMirroredShell = shellDir === "rtl" || baseLang === "ar";
  const isIncludeCompany = useIsIncludeCompany();
  const user = useUser();

  const { hasPermission } = usePermission();

  const sidebarItems = createSidebarItems(t, { isAgency: user?.isAgency, hasPermission });

  const hasSidebarChrome = isIncludeCompany;
  const sidebar = hasSidebarChrome ? (
    <SidebarLayout
      className="sticky top-0 max-h-screen min-h-0 h-full"
      items={sidebarItems}
    />
  ) : null;

  const main = (
    <main
      dir={useRtlMirroredShell ? "rtl" : "ltr"}
      lang={i18n.resolvedLanguage ?? i18n.language}
      className={cn(
        "min-h-0 min-w-0 scroll-y-overlay overflow-x-hidden bg-primary-foreground px-[2.62rem] pb-12 pt-[1rem]",
        useRtlMirroredShell && "rtl",
      )}
    >
      <Navbar />
      <Outlet />
    </main>
  );

  return (
    <SidebarProvider>
      <div
        dir="ltr"
        className={cn(
          "grid h-screen min-h-0 w-full grid-rows-1 items-stretch overflow-hidden text-foreground",
          !hasSidebarChrome && "grid-cols-1",
          hasSidebarChrome &&
            (useRtlMirroredShell
              ? "grid-cols-[minmax(0,1fr)_auto]"
              : "grid-cols-[auto_minmax(0,1fr)]"),
        )}
      >
        {hasSidebarChrome ? (
          useRtlMirroredShell ? (
            <>
              {main}
              {sidebar}
            </>
          ) : (
            <>
              {sidebar}
              {main}
            </>
          )
        ) : (
          main
        )}
      </div>
    </SidebarProvider>
  );
}
